/**
 * Invitation Link Modal - Copy Link Only
 *
 * File: Mythbound.App/src/components/campaign/InviteLinkModal.tsx
 * Created: 2026-01-06
 * Last Modified: 2026-01-06
 *
 * PURPOSE:
 * Modal for generating shareable campaign invitation links (copy link only).
 * Token-based invitation system with 7-day expiry (default).
 *
 * UI LAYOUT:
 * ┌───────────────────────────────┐
 * │ Invite to Campaign            │
 * ├───────────────────────────────┤
 * │ Campaign: "Forgotten Realms"  │
 * │                               │
 * │ [Generate Link]               │
 * │                               │
 * │ Shareable Link:               │
 * │ https://mythbound.app/join/   │
 * │ inv_a1b2c3d4e5f6g7h8         │
 * │                               │
 * │ [Copy Link] [✓ Copied]        │
 * │                               │
 * │ [Close]                       │
 * └───────────────────────────────┘
 *
 * KEY FEATURES:
 * - Generate new invitation link (token format: inv_a1b2c3d4e5f6g7h8)
 * - Link expires in 7 days (configurable)
 * - Copy to clipboard button with "Copied!" feedback
 * - Only GM can generate invitations
 * - Toast on copy: "Link copied to clipboard"
 *
 * VISUAL DESIGN:
 * - Clean tech UI (functional element, not parchment)
 * - Dark modal background with gold accents
 * - Green "Copied!" success feedback
 *
 * [FUTURE CODESPACE]
 * - Screen name search with identifier tags
 * - List pending invitations with revoke button
 * - Custom expiry duration selector
 *
 * NO REVOKE CONTROL HERE (PRD001A 001A.6 item 5, explicit decision): this
 * modal only ever tracks the single most-recently-generated link in local
 * state — it never lists multiple invitations. `InviteLinkPanel.tsx`
 * (`src/components/campaign/InviteLinkPanel.tsx`) is the one place that
 * displays a specific invitation with an id, so the revoke control lives
 * there instead. `CampaignService.revokeInvitation()` exists and is ready
 * to use here too, if this modal ever grows a real invitation list.
 *
 * Pipeline: UI → CampaignService.createInvitation → Backend → Database
 * Dependencies: React, campaignService
 */

import React, { useState } from 'react';
import { campaignService } from '../../services/api/CampaignService';
import { toastService } from '../../services/toast/toastService';

interface InviteLinkModalProps {
  campaignId: string;
  campaignName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const InviteLinkModal: React.FC<InviteLinkModalProps> = ({
  campaignId,
  campaignName,
  isOpen,
  onClose
}) => {
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateLink = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const invitation = await campaignService.createInvitation(campaignId);
      setGeneratedLink(invitation.shareable_url);
      setIsCopied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate invitation');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;
    
    try {
      await navigator.clipboard.writeText(generatedLink);
      setIsCopied(true);
      
      // Reset "Copied!" after 2 seconds
      setTimeout(() => setIsCopied(false), 2000);
      
      toastService.success('Link copied to clipboard');
    } catch (err) {
      setError('Failed to copy to clipboard');
      toastService.error('Failed to copy link');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1a1a1a',
          border: '2px solid #d4af37',
          borderRadius: '12px',
          padding: '32px',
          minWidth: '450px',
          maxWidth: '550px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
        }}
      >
        {/* Header */}
        <h2 style={{
          margin: 0,
          marginBottom: '8px',
          fontSize: '24px',
          color: '#d4af37',
          fontWeight: 'bold'
        }}>
          Invite to Campaign
        </h2>
        <div style={{
          fontSize: '14px',
          color: '#888',
          marginBottom: '24px'
        }}>
          Campaign: <span style={{ color: '#fff', fontWeight: '600' }}>{campaignName}</span>
        </div>

        {/* Generate Button (if no link yet) */}
        {!generatedLink && (
          <button
            onClick={handleGenerateLink}
            disabled={isGenerating}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              backgroundColor: '#d4af37',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: '8px',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              marginBottom: '20px'
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate Invitation Link'}
          </button>
        )}

        {/* Generated Link Display */}
        {generatedLink && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              fontSize: '14px',
              color: '#aaa',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Shareable Link:
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#d4af37',
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              marginBottom: '12px'
            }}>
              {generatedLink}
            </div>

            {/* Copy Button */}
            <button
              onClick={handleCopyLink}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '15px',
                fontWeight: '600',
                backgroundColor: isCopied ? '#4caf50' : '#333',
                color: isCopied ? '#fff' : '#d4af37',
                border: `2px solid ${isCopied ? '#4caf50' : '#d4af37'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {isCopied ? (
                <>
                  <span style={{ fontSize: '18px' }}>✓</span>
                  Copied to Clipboard!
                </>
              ) : (
                <>
                  <span style={{ fontSize: '18px' }}>📋</span>
                  Copy Link
                </>
              )}
            </button>

            <div style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '12px',
              textAlign: 'center'
            }}>
              {/* Corrected 2026-09-22 (PRD001A 001A.6): this token is
                  reusable, NOT one-time use — the backend never consumes it
                  on join (same token can be used by multiple players);
                  "one-time use" here was factually wrong. */}
              Link expires in 7 days • Reusable by multiple players until then
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#ffe0e0',
            border: '1px solid #c41e3a',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#c41e3a'
          }}>
            {error}
          </div>
        )}

        {/* [FUTURE CODESPACE] Screen name search */}
        {/* <div style={{
          padding: '16px',
          backgroundColor: '#2a2a2a',
          border: '1px solid #444',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '8px' }}>
            Or search by screen name:
          </div>
          <input
            type="text"
            placeholder="Search screen name (e.g., DragonSlayer#1234)"
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#1a1a1a',
              border: '1px solid #444',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '14px'
            }}
          />
        </div> */}

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '15px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            color: '#aaa',
            border: '2px solid #444',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

